import { useTheme } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useEntitiesFromSearch } from 'api/search'
import { useEffect, useMemo, useState } from 'react'
import { useUpdateItem } from '../../api/aws/useUpdateItem'
import { useUsers } from '../../api/aws/useUsers'
import { useLayoutContext } from '../../context/LayoutProvider/LayoutProvider'
import { useSnackbarContext } from '../../context/SnackbarProvider/SnackbarProvider'
import { aggregateStageTotals, formatCurrency } from './aggregateStageTotals'
import DealsKanban from './DealsKanban'
import './styles.css'

const updateDealStages = deals => {
  return deals?.map(deal => {
    if (deal?.stage === 'Qualified Lead') {
      deal.stage = 'Proposal'
    } else if (deal?.stage === 'New Lead') {
      deal.stage = 'Lead'
    } else if (deal?.stage === 'Proposed - Cold') {
      deal.stage = 'Cold'
    } else if (deal?.stage === 'Agreement Sent') {
      deal.stage = 'Contract Sent'
    }
    return deal
  })
}
const calculatePipelineMetrics = kanbanData => {
  const targetStages = ['Discovery', 'Pending Approval', 'Proposal', 'Contract Created', 'Contract Sent']

  let openDealsCount = 0
  let activePipelineIncome = 0

  targetStages.forEach(stage => {
    if (kanbanData[stage]) {
      openDealsCount += kanbanData[stage].items.length
      activePipelineIncome += kanbanData[stage].items.reduce((sum, deal) => {
        const dealTotal = deal.total ? parseFloat(deal.total) : 0
        return sum + dealTotal
      }, 0)
    }
  })

  return { openDealsCount, activePipelineIncome }
}

const calculateDealWonRevenue = kanbanData => {
  const targetStages = ['Deal Won']

  let dealWonRevenue = 0

  targetStages.forEach(stage => {
    if (kanbanData[stage]) {
      dealWonRevenue += kanbanData[stage].items.reduce((sum, deal) => {
        const dealTotal = deal.total ? parseFloat(deal.total) : 0
        return sum + dealTotal
      }, 0)
    }
  })

  return dealWonRevenue
}

const calculateWeightedPipeline = kanbanData => {
  const targetStages = ['Discovery', 'Pending Approval', 'Proposal', 'Contract Created', 'Contract Sent']

  let weightedPipelineTotal = 0

  targetStages.forEach(stage => {
    if (kanbanData[stage]) {
      weightedPipelineTotal += kanbanData[stage].items.reduce((sum, deal) => {
        // Ensure total is a valid number
        const dealTotal = deal.total ? parseFloat(deal.total.toString().replace(/[$,]/g, '')) : 0

        // Ensure probability is valid (default to 50% if empty)
        let probability = deal.probability ? parseFloat(deal.probability) : 50

        // Convert percentage to decimal (100% = 1, 50% = 0.5, etc.)
        const probabilityFactor = probability / 100

        return sum + dealTotal * probabilityFactor
      }, 0)
    }
  })

  return Math.round(weightedPipelineTotal) // Round to nearest whole number
}
const calculateDaysSinceModified = lastModified => {
  if (!lastModified) return 'N/A' // If no lastModified date, return N/A
  const modifiedDate = new Date(lastModified)
  const today = new Date()
  const timeDiff = today.getTime() - modifiedDate.getTime()
  return Math.floor(timeDiff / (1000 * 60 * 60 * 24)) // Convert ms → days
}
const loadDealsIntoKanban = (deals, extraStages, users, organizations) => {
  const defaultStages = {
    Discovery: { items: [], label: 'Discovery' },
    'Pending Approval': { items: [], label: 'Pending Approval' },
    Proposal: { items: [], label: 'Proposal' },
    'Contract Created': { items: [], label: 'Contract Created' },
    'Contract Sent': { items: [], label: 'Contract Sent' },
    'Deal Won': { items: [], label: 'Deal Won' },
  }

  const additionalStages = {
    Cold: { items: [], label: 'Cold' },
    Purgatory: { items: [], label: 'Lost' },
  }

  const stages = extraStages ? { ...defaultStages, ...additionalStages } : defaultStages

  // const sortedDeals = deals.sort((a, b) => {
  // const dateA = new Date(a.lastModified || 0).getTime()
  // const dateB = new Date(b.lastModified || 0).getTime()
  // return dateB - dateA
  // })
  const sortedDeals = [...deals].sort((a, b) => {
    const dateA = new Date(a.lastModified || 0).getTime()
    const dateB = new Date(b.lastModified || 0).getTime()
    return dateB - dateA
  })

  sortedDeals.forEach(deal => {
    const owner = users.find(user => user.user_uuid === deal?.owner?.[0])
    const organization = organizations.find(org => org.org_uuid === deal?.organizations?.[0])

    if (stages[deal.stage]) {
      const daysSinceModified = calculateDaysSinceModified(deal.lastModified)
      // stages[deal.stage].items.push({
      //   id: deal.id || deal.deal_uuid,
      //   primary: deal.name || deal.Deal_Name || deal.organizations.name,
      //   secondary: `Stage: ${deal.stage} - Modified: ${daysSinceModified} days ago`,
      //   details: `Contact Email: ${deal.contact?.email || 'No contact email'}, Website: ${
      //     deal.contact?.websiteUrl || 'No website URL'
      //   }, Total Value: ${deal.total || 'N/A'}`,
      //   ownerName: owner?.name,
      //   lastModified: deal.lastModified,
      //   daysSinceModified, // ✅ Include for debugging or sorting
      //   ...deal,
      //   organization: {
      //     name: organization?.name,
      //     vertical: organization?.vertical,
      //   },
      //   staleDays: daysSinceModified,
      // })
      stages[deal.stage].items.push({
        id: deal.id || deal.deal_uuid,
        primary: deal.name || deal.Deal_Name || deal.organizations?.name,
        secondary: `Stage: ${deal.stage} - Modified: ${daysSinceModified} days ago`,
        details: `Contact Email: ${deal.contact?.email || 'No contact email'}, Website: ${
          deal.contact?.websiteUrl || 'No website URL'
        }, Total Value: ${deal.total ? formatCurrency(deal.total) : '$0'}`, // ✅ Ensure total is formatted correctly
        total: deal.total || 0, // ✅ Default to 0 if missing
        ownerName: owner?.name,
        lastModified: deal.lastModified,
        staleDays: daysSinceModified,
        ...deal,
        organization: {
          name: organization?.name,
          vertical: organization?.vertical,
        },
      })
    }
  })

  return stages
}

const KanbanContainer = () => {
  const theme = useTheme()
  const { pinned } = useLayoutContext()
  const queryClient = useQueryClient()
  // const { pipelineDeals, isPipelineLoading, isPipelineError } = usePipelineDeals(true, ['pipeline', 'deals'])
  const { organizations, deals: pipelineDeals } = useEntitiesFromSearch()
  const { users, isUsersLoading, isUsersError } = useUsers(true, ['users'])
  const stablePipelineDeals = useMemo(() => pipelineDeals || [], [pipelineDeals])
  const stableUsers = useMemo(() => users || [], [users])
  const stableOrganizations = useMemo(() => organizations || [], [organizations])

  const [kanbanTotals, setKanbanTotals] = useState<{
    openDeals?: number
    activePipelineIncome?: number
    weightedPipeline?: number
    dealWonRevenue?: number
  }>({})
  const [stageTotals, setStageTotals] = useState({})
  const [extraStages, setExtraStages] = useState(false)
  const [totalSum, setTotalSum] = useState(0)
  const [isDragging, setIsDragging] = useState(false)

  const [totalGrossProfit, setTotalGrossProfit] = useState(0)
  const [totalDeals, setTotalDeals] = useState(0)
  const [localStageState, setLocalStageState] = useState({})
  const [kanbanData, setKanbanData] = useState({})
  const refinedDeals = useMemo(() => updateDealStages(pipelineDeals), [pipelineDeals])
  useEffect(() => {
    if (kanbanData) {
      setKanbanTotals(prev => ({
        ...prev,
        openDeals: calculatePipelineMetrics(kanbanData).openDealsCount,
        activePipelineIncome: calculatePipelineMetrics(kanbanData).activePipelineIncome,
        weightedPipeline: calculateWeightedPipeline(kanbanData),
        dealWonRevenue: calculateDealWonRevenue(kanbanData),
      }))
    }
  }, [kanbanData])
  // useEffect(() => {
  //   if (!isDragging && pipelineDeals && !isUsersLoading) {
  //     const stages = loadDealsIntoKanban(refinedDeals, extraStages, users, organizations)
  //     const totals = aggregateStageTotals(stages)
  //     setStageTotals(totals)
  //     setLocalStageState(totals)
  //     setKanbanData(stages) // ✅ Ensure this does not update while dragging
  //   }
  // }, [pipelineDeals, extraStages, users, organizations, isDragging])
  useEffect(() => {
    if (!isDragging && stablePipelineDeals.length > 0 && !isUsersLoading) {
      const stages = loadDealsIntoKanban(refinedDeals, extraStages, stableUsers, stableOrganizations)
      const totals = aggregateStageTotals(stages)
      setStageTotals(totals)
      setLocalStageState(totals)
      setKanbanData(stages)
    }
  }, [refinedDeals, extraStages, stableUsers, stableOrganizations, isDragging])

  const { mutate: updateItem } = useUpdateItem()
  const { showSnackbar } = useSnackbarContext()

  const handleDroppedUpdate = (fieldName, itemId, newValue) => {
    const PK = itemId?.split?.('::')?.[0]
    const SK = itemId?.split?.('::')?.[1]
    updateItem(
      {
        PK: PK,
        SK: SK,
        tableName: 'ORGANIZATIONS_TABLE',
        fieldName,
        newValue,
      },
      {
        onSuccess: (updates, variables) => {
          showSnackbar(`Deal ${fieldName} updated successfully`, 'success')
          console.log('Update successful')
        },
        onError: err => {
          console.error('Update failed', err)
        },
      }
    )
  }

  const onDragEnd = result => {
    const { source, destination } = result
    if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) return
    console.log('source', source)
    console.log('destination', destination)
    handleDroppedUpdate('stage', result.draggableId, destination.droppableId)

    // setKanbanData(prevData => {
    //   const newData = { ...prevData }

    //   const sourceColumn = [...newData[source.droppableId].items]
    //   const destinationColumn = [...newData[destination.droppableId].items]

    //   const [movedItem] = sourceColumn.splice(source.index, 1)
    //   destinationColumn.splice(destination.index, 0, movedItem)

    //   return {
    //     ...newData,
    //     [source.droppableId]: { ...newData[source.droppableId], items: sourceColumn },
    //     [destination.droppableId]: { ...newData[destination.droppableId], items: destinationColumn },
    //   }
    // })
    setKanbanData(prevData => {
      if (source.droppableId === destination.droppableId && source.index === destination.index) {
        return prevData
      }

      const newData = { ...prevData }
      const sourceColumn = [...newData[source.droppableId].items]
      const destinationColumn = [...newData[destination.droppableId].items]

      const [movedItem] = sourceColumn.splice(source.index, 1)
      destinationColumn.splice(destination.index, 0, movedItem)

      return {
        ...newData,
        [source.droppableId]: { ...newData[source.droppableId], items: sourceColumn },
        [destination.droppableId]: { ...newData[destination.droppableId], items: destinationColumn },
      }
    })
  }

  const testLoading = true
  const isLoading = isUsersLoading
  return (
    <DealsKanban
      kanbanData={kanbanData}
      stageTotals={stageTotals}
      extraStages={extraStages}
      setExtraStages={setExtraStages}
      totalSum={totalSum}
      totalGrossProfit={totalGrossProfit}
      totalDeals={kanbanTotals?.openDeals ?? 0}
      activePipeline={kanbanTotals?.activePipelineIncome ?? 0}
      weightedPipeline={kanbanTotals?.weightedPipeline ?? 0}
      dealWonRevenue={kanbanTotals?.dealWonRevenue ?? 0}
      onDragEnd={onDragEnd}
      isLoading={isLoading}
    />
  )
}

export default KanbanContainer
